import axios from "axios";
import { getApiBaseUrl } from "@/utils/settingMer";
import BASE_PATH from "@/config/basePath";
import { message } from "antd";
import { getCookie } from "cookies-next";
import Router from "next/router";
import { getTranslatedTextByCode } from "@/utils/index";
import { isAuthBootstrapping } from "@/utils/authBootstrap";
import { clearAuthCookies } from "@/utils/cookie";
import { clientStore } from "@/store";
import { setToken, setUserInfo } from "@/store/user";
import { setCartTotal } from "@/store/cart";

// do not prompt message
const noErrorMsgUrls = ["/front/signIn"];

// server or client
const isServer = typeof window === "undefined";

// axios create
const service = axios.create({
  // baseURL: SettingMer.apiBaseURL,
  timeout: 60000, // 过期时间,
  // withCredentials: true,
});

// request interceptor
service.interceptors.request.use(
  (config: any) => {
    if (config.url.indexOf("/v1/public/getFooter") > -1) {
      // footer 走本站服务端代理（pages/api/footer.ts）：apiKey 由服务端附加，
      // 不再下发到浏览器。上游地址与鉴权头都在服务端决定。
      config.baseURL = `${BASE_PATH}/api`;
      config.url = "/footer";
    } else {
      // app request
      config.baseURL = getApiBaseUrl("default").apiBaseURL;
      const token = getCookie("token");
      if (token) {
        config.headers["Authori-zation"] = "TOKEN_USER:" + token;
      }
    }

    if (/get/i.test(config.method)) {
      config.params = config.params || {};
    }
    // config["Content-Type"] = "application/x-www-form-urlencoded";
    // console.log(config);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// response interceptor
service.interceptors.response.use(
  async (response) => {
    const res = response.data;
    const { url } = response.config;
    // if the custom code is not 200, it is judged as an error.
    if (res.code === 401) {
      // 跳转登录引导期间（appToken/crm/站点跳转换取 token 的窗口），
      // 不因个别抢跑的 401 请求删除正在成形的登录态并跳登录页，避免偶发被踢回登录页。
      // 引导窗口外也可能收到抢跑请求的 401：落地页一挂载就发的请求还没带 token，响应却在交接完成后才回来
      // （跨站交接只调一次 getSession，窗口很短；App 直开 /user/voucher 跨站即此情况）。
      // 发出时用的不是当前 token 的 401 一律视为过期响应，不清登录态。
      const currentToken = isServer ? undefined : getCookie("token");
      const stale = !!currentToken && response.config.headers?.["Authori-zation"] !== "TOKEN_USER:" + currentToken;
      // 免登参数（appToken / token）还在地址栏 = 换票没走完（RedirectSignIn 成功失败都会清掉）。
      // 静态页 router.isReady 晚于首屏请求，抢跑的 401 可能在引导开关打开前就回来，不能据此判未登录
      const handoffPending = !isServer && /[?&](appToken|token)=/.test(window.location.search);
      if (!isServer && !isAuthBootstrapping() && !stale && !handoffPending) {
        clearAuthCookies();
        clientStore?.dispatch(setToken(undefined));
        clientStore?.dispatch(setUserInfo(undefined));
        clientStore?.dispatch(setCartTotal(0));
        const { router } = Router;
        router?.push(`/login?redirect=${router.asPath}`);
      } else {
        console.log("login expired");
      }
    } else if (res.code === 403) {
      if (!isServer) {
        message.error(await getTranslatedTextByCode(403));
      } else {
        console.log("No access permission");
      }
    }
    if (res.code === 3806) {
      return res; //OTP验证码错误返回所有内容
    }
    if (res.code !== 200 && res.code !== 401 && res.code !== 0) {
      if (!isServer && url && noErrorMsgUrls.indexOf(url) < 0) {
        message.error((await getTranslatedTextByCode(res.code)) || "Error");
      } else {
        console.log((await getTranslatedTextByCode(res.code)) || "Error");
      }
      // 把响应体（含 code/message）传给调用方，便于上层拿到后端错误信息
      return Promise.reject(res);
    } else {
      return res.data;
    }
  },
  (error) => {
    if (!isServer) {
      message.error(error.message || "Error");
    } else {
      console.log(error.message || "Error");
    }
    return Promise.reject(error);
  }
);

export default service;
